import alt from "../alt";
import PersonStore from "../stores/PersonStore";
import PersonActions from "../actions/PersonActions";
import WishlistActions from "../actions/WishlistActions";
import WishlistUtil from "../utils/WishlistUtil";

import GlobalNotificationActions from "../actions/GlobalNotificationActions";
import OrderActions from "../actions/OrderActions";
import EventStore from "../stores/EventStore";
import FilmStore from "../stores/FilmStore";
import { history } from "../util";

const DEFAULT_WISHLIST = {
  events: [],
  films: []
};
const makeEmptyDiff = () => ({
  events: { add: [], remove: [] },
  films: { add: [], remove: [] }
});

const SHOULD_PERSIST = "localStorage" in window;
const LS_KEY = "eventive-wishlist_v2";
let cachedWishlist;
try {
  cachedWishlist =
    (SHOULD_PERSIST &&
      window.localStorage[LS_KEY] &&
      JSON.parse(window.localStorage[LS_KEY])) ||
    DEFAULT_WISHLIST;
} catch (e) {
  // Catch if unable to access localstorage (e.g. due to DOM Exception 18)
  cachedWishlist = DEFAULT_WISHLIST;
}

class WishlistStore {
  constructor() {
    this.bindActions(WishlistActions);

    this.state = {
      hasFetched: false,
      wishlist: DEFAULT_WISHLIST,
      _diff: makeEmptyDiff()
    };

    this.exportPublicMethods({
      isWishlisted: this.isWishlisted.bind(this),
      getWishlist: this.getWishlist.bind(this),
      getWishlistFilms: this.getWishlistFilms.bind(this),
      getWishlistEvents: this.getWishlistEvents.bind(this),
      getUnticketedWishlistEvents: this.getUnticketedWishlistEvents.bind(this)
    });
  }

  isWishlisted({ id, type }) {
    return this.state.wishlist[type].indexOf(id) !== -1;
  }

  getWishlist() {
    return this.state.wishlist;
  }

  getWishlistFilms() {
    return [].concat(this.state.wishlist.films);
  }

  getWishlistEvents() {
    return [].concat(this.state.wishlist.events);
  }

  getUnticketedWishlistEvents() {
    return this.state.wishlist.events
      .map(id => {
        let e = EventStore.getEventById(id);
        if (
          e &&
          e.ticket_buckets.filter(
            b => b.public && !b.applicable_pass_buckets.length
          ).length &&
          (!e.person || e.person.ticket_count === 0)
        ) {
          return e;
        } else {
          return null;
        }
      })
      .filter(e => !!e);
  }

  onOrderUnticketed() {
    let subitems = this.getUnticketedWishlistEvents()
      .map(e => {
        return e.ticket_buckets
          .filter(b => b.public && !b.applicable_pass_buckets.length)
          .map((b, i) => {
            return {
              ticket_bucket: b.id,
              quantity: i === 0 ? 1 : 0
            };
          });
      })
      .reduce((a, b) => a.concat(b), []);

    OrderActions.updateCart.defer({
      subitems: subitems.concat(
        TENANT.donate_inline_order_prompt ? [{ inline_donation: true }] : []
      )
    });
    setTimeout(
      () =>
        history.push({
          pathname: `/schedule/checkout`,
          state: { fromListing: true }
        }),
      0
    );
  }

  onFetch() {
    let { token } = PersonStore.getState();
    if (token) {
      WishlistUtil.get(token).then(({ events, films }) => {
        // Only populate if the fetched wishlist has at least one item -- otherwise,
        // don't overwrite the locally stored wishlist. This way, starring & then creating
        // an account preserves the wishlist.
        if ((events && events.length) || (films && films.length)) {
          this.setState({
            hasFetched: true,
            wishlist: {
              events: events.length ? events : [],
              films: films.length ? films : []
            }
          });
        } else {
          this.setState({ hasFetched: true });
        }
      });
    } else {
      // They're not logged in, try to load from cache
      if (SHOULD_PERSIST && cachedWishlist.wishlist) {
        this.setState({
          hasFetched: true,
          wishlist: cachedWishlist.wishlist || DEFAULT_WISHLIST
        });
      } else {
        this.setState({ hasFetched: true, wishlist: DEFAULT_WISHLIST });
      }

      // Also add an on-login-hook to fetch
      PersonActions.addOneTimeLoginSucceededHook.defer({
        fn: () => {
          WishlistActions.fetch.defer();
        }
      });
    }
  }

  onAdd({ id, type }) {
    let { wishlist, _diff } = this.state;
    if (wishlist[type].indexOf(id) === -1) {
      wishlist[type].push(id);
      _diff[type].add.push(id);
    }
    this.setState({ wishlist, _diff });
    if (type === "events") {
      GlobalNotificationActions.notify.defer({
        message: `"${EventStore.getEventById(id).name}" starred.`,
        action: {
          label: "View starred",
          onClick: () => history.push(`/schedule?justMyEvents=true`)
        }
      });
    } else if (type === "films") {
      GlobalNotificationActions.notify.defer({
        message: `"${FilmStore.getFilmById(id).name}" starred.`,
        action: {
          label: "View starred",
          onClick: () => history.push(`/films?starred=true`)
        }
      });
    }
  }

  onRemove({ id, type }) {
    let { wishlist, _diff } = this.state,
      index = wishlist[type].indexOf(id);
    wishlist[type].splice(index, 1);
    _diff[type].remove.push(id);
    this.setState({ wishlist, _diff });
    if (type === "events") {
      GlobalNotificationActions.notify.defer({
        message: `"${EventStore.getEventById(id).name}" unstarred.`,
        action: {
          label: "View starred",
          onClick: () => history.push(`/schedule?justMyEvents=true`)
        }
      });
    } else if (type === "films") {
      GlobalNotificationActions.notify.defer({
        message: `"${FilmStore.getFilmById(id).name}" unstarred.`,
        action: {
          label: "View starred",
          onClick: () => history.push(`/films?starred=true`)
        }
      });
    }
  }
}

let store = alt.createStore(WishlistStore, "WishlistStore");

store.listen(state => {
  // Wait until it's fetched
  if (!state.hasFetched) {
    return;
  }

  // If no changes, nothing to persist
  if (
    !Object.values(state._diff).some(d => Object.values(d).some(v => v.length))
  ) {
    return;
  }

  // Persist locally
  if (SHOULD_PERSIST) {
    try {
      window.localStorage[LS_KEY] = JSON.stringify({
        wishlist: state.wishlist
      });
    } catch (e) {
      // fall through - quota exceeded
    }
  }

  // If logged in, persist to backend
  let { token } = PersonStore.getState();
  if (token) {
    WishlistUtil.update(token, state.wishlist, state._diff);
    state._diff = makeEmptyDiff();
  }
});

// Initial fetch
WishlistActions.fetch.defer();

export default store;



// WEBPACK FOOTER //
// ./src/app/stores/WishlistStore.js